import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { PencilIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import CustomerDetails from './CustomerDetails';
import DrawerPortal from '../../../Common/SidebarDrawer/DrawerPortal';
import UpdateCustomerForm from '../../CustomersTable/Forms/UpdateCustomerForm';

const Profile = forwardRef(({ customer, setCustomerData, onMount, onUnmount }, ref) => {

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const drawerRef = useRef(null);
  const modalRef = useRef(null);
  const {
    firstname,
    lastname,
    birthdate,
    addresses,
    profilePictureUrl,
  } = customer || {};

  const initials = firstname.charAt(0) + lastname.charAt(0);

  // Fonction pour calculer l'âge exact au jour près
  const calculateAge = (birthdate) => {
    if (!birthdate) return null;

    const today = new Date();
    const birthDate = new Date(birthdate);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Si l'anniversaire n'est pas encore passé cette année
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const handleDrawer = (e) => {
    e.stopPropagation()
    if (drawerRef.current) {
      drawerRef.current.showDrawer();
    }
  };

  const handleModal = (e) => {
    e.stopPropagation()
    if (modalRef.current) {
      modalRef.current.showModal();
    }
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const openImageModal = () => {
    setIsImageModalOpen(true);
    setIsImageLoading(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleUpdateProfile = (values) => {
    setCustomerData((prev) => ({ ...prev, ...values }));
  }

  useEffect(() => {
    onMount?.();
    return () => onUnmount?.();
  }, []);

  return (
    <>
      <div ref={ref} className="bg-base-100 h-fit relative z-50 lg:min-w-80 lg:w-2/4 lg:max-w-96 rounded-lg shadow-md px-4 py-4 flex flex-col items-center border-container">
        <div className="w-[95%]  top-4 flex gap-x-4">
          <PencilIcon onClick={handleDrawer} className="w-6 h-6 hover:stroke-primary transition-all cursor-pointer" />
          <ChevronDownIcon
            className="w-6 h-6 ml-auto hover:stroke-primary transition-all cursor-pointer"
            onClick={toggleCollapse}
          />
        </div>
        <div className="flex-shrink-0  w-36 h-36 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 mb-6">
          {
            profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt="Profile"
                className="w-full h-full object-cover rounded-full cursor-pointer"
                onClick={openImageModal}
              />
            ) : (
              <span className="text-xl font-bold">{initials}</span>
            )
          }
        </div>
        <div className="text-center">
          <div className="text-lg font-bold ">
            {firstname} {lastname}
          </div>
          {birthdate && (
            <div className="text-sm">
              {calculateAge(birthdate)} ans
            </div>
          )}
        </div>
        <div className={`flex w-11/12 flex-col gap-y-4  transition-all duration-300 ${isCollapsed ? 'max-h-0 overflow-hidden delay-150' : 'max-h-screen'}`}>
          <div className={`rounded-lg  p-4 transition-all delay-150 duration-300 ${isCollapsed ? 'opacity-0' : 'opacity-100'}`}>
            <CustomerDetails addresses={addresses} customer={customer} />
          </div>
        </div>
      </div>
      <DrawerPortal direction={'end'} ref={drawerRef}>
        <UpdateCustomerForm customer={customer} updateCustomer={handleUpdateProfile} />
      </DrawerPortal>

      {/* Image Modal */}
      {isImageModalOpen && profilePictureUrl && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[1000]" onClick={closeImageModal}>
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              className="absolute top-4 right-4 bg-white rounded-full p-1"
              onClick={closeImageModal}
            >
              <XIcon className="w-6 h-6" />
            </button>
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <img
              src={profilePictureUrl}
              alt="Profile"
              className={`max-w-full max-h-[90vh] object-contain ${isImageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
              onClick={(e) => e.stopPropagation()}
              onLoad={handleImageLoad}
            />
          </div>
        </div>
      )}
    </>
  );
});

export default Profile;
